/**
 * Re-apply the prior expansion state (`isExpanded` + already-loaded `children`)
 * of pinned wrapper rows onto a freshly-rebuilt pinned-children array.
 *
 * Why: the pin-mutation and query-cache subscribers in `DatabaseExplorer.tsx`
 * rebuild the connection's pinned rows from scratch via `buildPinnedChildren`,
 * whose factory output is always collapsed (`isExpanded: false`, `children: []`).
 * Without this merge, any pin add / unpin / reorder OR a background resolution
 * upgrade collapses every pinned subtree the user had drilled into (review
 * finding P2). The rebuild still owns the authoritative top-level fields
 * (order, `resolution`, freshly-stamped metadata) — we only carry over the
 * navigational state the rebuild can't reconstruct.
 *
 * Pure and side-effect-free. Matching is by node `id` (stable across reorders),
 * so a reordered-but-still-present pin keeps its expansion.
 */

import type { DatabaseExplorerNode } from '../../../explorer/models/nodeTypes';

export function preservePinnedExpansionState(
  next: DatabaseExplorerNode[],
  prev: DatabaseExplorerNode[],
): DatabaseExplorerNode[] {
  if (prev.length === 0) return next;

  const prevById = new Map<string, DatabaseExplorerNode>();
  for (const node of prev) prevById.set(node.id, node);

  return next.map((node) => {
    const previous = prevById.get(node.id);
    if (!previous) return node;
    // Nothing drilled-into to preserve — leave the fresh (collapsed) node as-is.
    if (!previous.isExpanded && (previous.children?.length ?? 0) === 0) return node;
    return {
      ...node,
      isExpanded: previous.isExpanded,
      children: previous.children,
    } as DatabaseExplorerNode;
  });
}
